package com.advaita.cloud.drive.ui

import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import com.advaita.cloud.drive.ui.api.AuthApi
import com.advaita.cloud.drive.ui.api.FilesApi
import com.advaita.cloud.drive.ui.store.FileBrowserStore
import com.advaita.cloud.drive.ui.store.PreviewStore
import com.advaita.cloud.drive.ui.store.PreviewTooltipStore
import com.advaita.cloud.drive.ui.store.SearchStore
import com.advaita.cloud.drive.ui.store.DriveView
import com.advaita.cloud.drive.ui.store.isSharedByMeUrl
import com.advaita.cloud.drive.ui.store.isSharedViewUrl
import com.advaita.cloud.drive.ui.store.viewForUrl
import com.advaita.cloud.drive.ui.store.ShareDialogStore
import com.advaita.cloud.drive.ui.store.SharedWithMeStore
import com.advaita.cloud.drive.ui.store.SharedByMeStore
import com.advaita.cloud.drive.ui.store.MyAccessStore
import com.advaita.cloud.drive.ui.components.ToastLevel
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.events.KeyboardEvent

fun main() {
    // Echo the build baked into THIS bundle. If a hard-refresh doesn't change the
    // sha here, the browser is serving a cached frontend.js (not the latest deploy).
    console.log("📦 Advaita Data Hub — ${BuildInfo.VERSION} (${BuildInfo.GIT_SHA})")

    // ADV-1497: first, so the view seeding below and the Account menu read the restored query.
    AuthApi.restoreReturn()

    // ADV-1330 F3: Shared with me. Its own store, like the dialog's. The file browser reads its
    // open folder, so a create in Shared with me goes there (the shared-folder create slice).
    val sharedWithMe = SharedWithMeStore()
    // CR-6.3, CR-6.5: Shared by me and the Account menu (My access). Their own stores.
    val sharedByMe = SharedByMeStore()
    val myAccess = MyAccessStore()
    val store = FileBrowserStore(sharedWithMe)
    val preview = PreviewStore()

    // The picker peeks at a file's head on hover instead of docking a panel — a
    // panel beside a centred dialog never sat right. Its own store, because a
    // hover peek shares no state with the docked pane.
    val pickerPeek = PreviewTooltipStore()

    // Spotlight search (ADV-1215) — its own store, opened from the header
    // icon or the global Cmd/Ctrl-K shortcut below.
    val searchStore = SearchStore(
        search = { path, pattern, limit -> FilesApi.searchFiles(path, pattern, limit) },
        searchFolder = { folderId, pattern, limit, pageToken -> FilesApi.searchFolder(folderId, pattern, limit, pageToken) },
    )

    // ADV-1330: the share dialog. Its own store: the picker's names live only in it, and
    // closing the dialog drops them (no browser storage, S4).
    val shareDialog = ShareDialogStore()

    // After Leave in the dialog, the caller may no longer reach the file: re-read both lists.
    shareDialog.onLeft = { left, done ->
        store.toast(done, ToastLevel.SUCCESS)
        // Gone from Shared with me at once, so an outage on the re-read cannot keep it on screen.
        sharedWithMe.forget(left.id)
        MainScope().launch {
            if (store.view == DriveView.SHARED) sharedWithMe.load() else store.refresh()
        }
    }
    // Leave asks first, everywhere (coordinator decision (c)): the app's own confirm dialog.
    shareDialog.confirmLeave = { p -> store.confirm(title = p.title, message = p.message, confirmLabel = "Leave") }
    shareDialog.notify = { message -> store.toast(message, ToastLevel.SUCCESS) }

    // Seed currentPath from the URL on first load so deep links work. A Shared with me deep
    // link skips My Drive's listing: App's startup reads Shared with me instead.
    if (isSharedViewUrl(window.location.search)) store.showSharedWithMe(fromUrl = true)
    else if (isSharedByMeUrl(window.location.search)) store.showSharedByMe(fromUrl = true)
    else store.setPathFromUrl(pathFromLocation())

    // Browser back/forward updates the store without pushing a new entry.
    window.addEventListener("popstate", {
        // Gated on the share UI: with sharing off, Back to a ?view=shared entry stays on My Drive.
        when (viewForUrl(window.location.search, store.shareUiVisible)) {
            DriveView.SHARED -> {
                store.showSharedWithMe(fromUrl = true)
                MainScope().launch { sharedWithMe.load() }
            }
            DriveView.SHARED_BY_ME -> {
                store.showSharedByMe(fromUrl = true)
                MainScope().launch { sharedByMe.load() }
            }
            DriveView.MY_DRIVE -> store.setPathFromUrl(pathFromLocation())
        }
    })

    // The preview pane's width is persisted, so a width chosen on a large
    // monitor can arrive on a small one. The page no longer scrolls, so an
    // over-wide pane would squeeze the table with no way out.
    window.addEventListener("resize", {
        preview.reclampWidth()
    })

    // Global spotlight shortcut. Registered once here (not per-composition)
    // so it works even before the first render and never double-attaches.
    document.addEventListener("keydown", { e ->
        val ke = e as KeyboardEvent
        val target = ke.target as? org.w3c.dom.Element
        val inEditable = target != null && (target.tagName == "INPUT" || target.tagName == "TEXTAREA")
        if (ke.key.lowercase() == "k" && (ke.metaKey || (ke.ctrlKey && !inEditable))) {
            ke.preventDefault()
            // Not over the share dialog: two dialogs would fight for the keyboard.
            // ADV-1330: in Shared with me, the open shared folder. The roots list has no search.
            if (AuthApi.currentToken() != null && !shareDialog.isOpen) {
                if (store.view == DriveView.SHARED) {
                    sharedWithMe.currentFolder?.let { searchStore.openShared(it, root = sharedWithMe.trail.first()) }
                }
                else searchStore.open(store.currentPath)
            }
        }
        if (ke.key == "Escape" && searchStore.isOpen) searchStore.close()
    })

    renderComposable(rootElementId = "root") {
        App(store, preview, pickerPeek, searchStore, shareDialog, sharedWithMe, sharedByMe, myAccess)
    }
}

/** Convert window.location.pathname to a Drive logical path. */
private fun pathFromLocation(): String {
    val raw = window.location.pathname
    // Strip the /ui prefix; whatever's left is the folder path.
    val stripped = raw.removePrefix("/ui").removePrefix("/")
    if (stripped.isBlank()) return "/"
    // pathname is percent-encoded; decode each segment so the logical path
    // round-trips with display names containing spaces, etc.
    val decoded = stripped.split("/")
        .filter { it.isNotBlank() }
        .joinToString("/") { decodeURIComponent(it) }
    return "/$decoded"
}

private fun decodeURIComponent(value: String): String = js("decodeURIComponent(value)") as String
